import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { getCapabilitiesFromUser, hasAnyCapability } from '../auth/access';
import type {
  OverviewControlSummary,
  OverviewControlTopPattern,
} from '../domain/overview/overviewTypes';
import { mapOverviewControlSummaryResponse } from '../domain/overview/overviewControlSummary';
import { overviewApi, type OverviewControlSummaryResponse } from '../services/overviewApi';
import {
  countActiveSafetyCases,
  hasSafetyCaseId,
} from '../domain/overview/overviewProductModel';
import { buyerPatternDisplayLabel } from '../domain/overview/overviewBuyerDisplay';
import SiteConditionStatePanel from '../components/siteView/SiteConditionStatePanel';
import { buildSellableShellHref } from '../domain/sellableSiteExperience/shellContext';

export type SiteViewProjection = {
  validScans: OverviewControlTopPattern[];
  signedScans: OverviewControlTopPattern[];
  scansInProgress: OverviewControlTopPattern[];
  patternsAwaitingScan: OverviewControlTopPattern[];
  attentionRecords: OverviewControlTopPattern[];
  hotspotCount: number;
  activeSafetyCaseCount: number;
};

type SiteViewSummary = {
  current_valid_scans?: number;
  recurring_hotspots_requiring_review?: number;
  classification_attention_count?: number;
  active_safety_cases_across_site?: number;
  signed_safety_control_scans?: number;
  scans_in_progress?: number;
  patterns_awaiting_scan?: number;
};

export type SiteViewTimingStatus =
  | 'concentration_found'
  | 'no_concentration'
  | 'insufficient_measurement'
  | 'unavailable';

export type SiteViewTiming = {
  status?: SiteViewTimingStatus | string | null;
  window_label?: string | null;
  concentration_percent?: number | null;
  timezone?: string | null;
  axis_label?: string | null;
  source?: string | null;
  reason?: string | null;
};

export type SiteViewScanMeasurement = {
  source?: string | null;
  scan_id?: string | null;
  report_id?: string | null;
  finalized_at?: string | null;
  window_start_at?: string | null;
  window_end_at?: string | null;
  timezone?: string | null;
  detector_record_count?: number | null;
  candidate_episode_count?: number | null;
  confirmed_episode_count?: number | null;
  representative_evidence_count?: number | null;
  usable_hours?: number | null;
  measurement_status?: 'MET' | 'NOT_MET' | string | null;
  customer_decision_status?: string | null;
  customer_decision?: string | null;
  operational_finding?: string | null;
};

type SiteViewRegisterRow = {
  pattern_label?: string | null;
  policy_id?: string | null;
  incident_type?: string | null;
  camera_id?: string | null;
  zone_id?: string | null;
  group_key?: string | null;
  scan_key?: string | null;
  qualifying_entry_count?: number | null;
  buyer_surface?: string | null;
  control_status?: string | null;
  safety_case_id?: string | null;
  finding_id?: string | null;
  safety_case_url?: string | null;
  link_to_pattern_review?: string | null;
  latest_signed_scan_id?: string | null;
  active_draft_report_id?: string | null;
  timing?: SiteViewTiming | null;
  scan_measurement_summary?: SiteViewScanMeasurement | null;
  [key: string]: unknown;
};

type SiteViewResponse = OverviewControlSummaryResponse & {
  site_view?: {
    summary?: SiteViewSummary;
    valid_scan_register?: SiteViewRegisterRow[];
    signed_scan_register?: SiteViewRegisterRow[];
    scans_in_progress?: SiteViewRegisterRow[];
    patterns_awaiting_scan?: SiteViewRegisterRow[];
    classification_attention?: SiteViewRegisterRow[];
  };
};

export function buildSiteViewProjection(
  summary: OverviewControlSummary | null | undefined,
  siteSummary?: SiteViewSummary,
): SiteViewProjection {
  const validScans = summary?.topPatterns || [];
  const attentionRecords = (summary?.classificationAttentionPatterns || []).filter(
    (row) => row.buyerSurface === 'classification_attention',
  );
  const signedScans = validScans.filter(
    (row) => row.scanMeasurementSummary?.source === 'signed_scan_report_v1',
  );
  const scansInProgress = validScans.filter((row) => Boolean(row.activeDraftReportId));
  const patternsAwaitingScan = validScans.filter(
    (row) =>
      row.scanMeasurementSummary?.source !== 'signed_scan_report_v1' &&
      !row.activeDraftReportId,
  );
  return {
    validScans,
    signedScans,
    scansInProgress,
    patternsAwaitingScan,
    attentionRecords,
    hotspotCount:
      siteSummary?.recurring_hotspots_requiring_review ??
      summary?.exposurePatternsNeedingReview ??
      validScans.filter((row) => row.buyerSurface === 'recurring_hotspot').length,
    activeSafetyCaseCount:
      siteSummary?.active_safety_cases_across_site ??
      countActiveSafetyCases([...validScans, ...attentionRecords]),
  };
}

function decisionStatus(row: OverviewControlTopPattern): string {
  if (row.hasNewActivitySinceLatestSignedScan) return 'Recheck running';
  if (String(row.correctiveActionStatus || '').trim()) return 'Action recorded';
  if (String(row.reviewStatus || '').trim().toLowerCase() === 'reviewed') {
    return 'Continue monitoring';
  }
  return 'Needs review';
}

function latestActivity(row: OverviewControlTopPattern): string {
  const value = row.latestObservationAt || row.latestSignedScanAt;
  if (!value) return 'No dated activity available';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? 'No dated activity available' : parsed.toLocaleString();
}

function attentionReason(row: OverviewControlTopPattern): string {
  if (!String(row.zoneId || '').trim()) return 'Missing zone';
  return 'Classification required';
}

/** Site View must keep policy × camera × zone distinguishable — never collapse to one generic label. */
export function siteViewPolicyLabel(row: OverviewControlTopPattern): string {
  return buyerPatternDisplayLabel(row);
}

export function siteViewPolicySecondary(
  row: OverviewControlTopPattern,
  policyId?: string | null,
): string {
  const normalizedPolicyId = String(policyId || '').trim();
  if (normalizedPolicyId) return `Policy ID: ${normalizedPolicyId}`;
  const incidentType = String(row.incidentType || '').trim();
  return incidentType ? `Type: ${incidentType}` : 'Policy ID unavailable';
}

export function siteViewCameraLabel(row: OverviewControlTopPattern): string {
  return String(row.cameraId || '').trim() || 'Camera missing';
}

export function siteViewZoneLabel(row: OverviewControlTopPattern): string {
  return String(row.zoneId || '').trim() || 'Zone missing';
}

export function siteViewIdentityLabel(row: OverviewControlTopPattern): string {
  return `${siteViewPolicyLabel(row)} · ${siteViewCameraLabel(row)} · ${siteViewZoneLabel(row)}`;
}

export function formatSiteViewTimingLabel(timing?: SiteViewTiming | null): string {
  const status = String(timing?.status || '').trim().toLowerCase();
  const windowLabel = String(timing?.window_label || '').trim();
  const timezone = String(timing?.timezone || '').trim();
  const tzSuffix = timezone ? ` (${timezone})` : '';
  if (status === 'concentration_found' && windowLabel) {
    const pct =
      typeof timing?.concentration_percent === 'number' &&
      Number.isFinite(timing.concentration_percent)
        ? ` · ${timing.concentration_percent}% of measured day-hours`
        : '';
    return `Concentration ${windowLabel}${pct}${tzSuffix}`;
  }
  if (status === 'no_concentration') {
    return `Measured — no time concentration${tzSuffix}`;
  }
  if (status === 'insufficient_measurement') {
    return `Insufficient measurement for timing${tzSuffix}`;
  }
  return 'Timing unavailable — no trusted concentration window is exposed for this scan.';
}

function formatMeasuredCount(value: number | null | undefined, singular: string, plural: string): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 'Not available';
  return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
}

function ScanMeasurementResult({
  measurement,
  state = 'signed',
}: {
  measurement?: SiteViewScanMeasurement | null;
  state?: 'signed' | 'in_progress' | 'awaiting';
}) {
  const confirmed = measurement?.confirmed_episode_count;
  const usableHours = measurement?.usable_hours;
  const evidenceCount = measurement?.representative_evidence_count;
  const gate = String(measurement?.measurement_status || '').trim().toUpperCase();

  if (!measurement || measurement.source !== 'signed_scan_report_v1') {
    const title =
      state === 'awaiting'
        ? 'Not measured — no Safety Control Scan'
        : state === 'in_progress'
        ? 'Measurement not finalized'
        : 'Signed measurement unavailable';
    const detail =
      state === 'awaiting'
        ? 'Current hotspot activity is a scan candidate, not a scan result.'
        : state === 'in_progress'
        ? 'A draft scan exists, but no signed result is available yet.'
        : 'Open the signed report for its authoritative result.';
    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {detail}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={0.75} alignItems="center" useFlexGap flexWrap="wrap">
        <Typography variant="body2" sx={{ fontWeight: 900 }}>
          {formatMeasuredCount(confirmed, 'confirmed episode', 'confirmed episodes')}
        </Typography>
        {gate ? (
          <Chip
            size="small"
            color={gate === 'MET' ? 'success' : 'warning'}
            label={`Measurement gate ${gate}`}
          />
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
        {typeof usableHours === 'number'
          ? `${usableHours.toLocaleString()} usable hours`
          : 'Usable hours unavailable'}
        {' · '}
        {formatMeasuredCount(evidenceCount, 'review item', 'review items')}
      </Typography>
    </Box>
  );
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return (
    <Paper variant="outlined" sx={{ p: 1.5, minHeight: 96 }}>
      <Typography variant="h5" sx={{ fontWeight: 900 }}>
        {value.toLocaleString()}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, fontWeight: 700 }}>
        {label}
      </Typography>
    </Paper>
  );
}

const AcceptanceSiteView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { effectiveUser } = useAuth();
  const canAccessScheduledScans = hasAnyCapability(
    getCapabilitiesFromUser(effectiveUser),
    ['scan:configure', 'scan:run'],
  );
  const [summary, setSummary] = useState<OverviewControlSummary | null>(null);
  const [siteSummary, setSiteSummary] = useState<SiteViewSummary | undefined>();
  const [timingByKey, setTimingByKey] = useState<Record<string, SiteViewTiming>>({});
  const [measurementByKey, setMeasurementByKey] = useState<
    Record<string, SiteViewScanMeasurement>
  >({});
  const [policyIdByKey, setPolicyIdByKey] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    overviewApi
      .getControlSummary({ hours: 168, top_limit: 25 })
      .then((response) => {
        if (cancelled) return;
        const payload = response as SiteViewResponse;
        const siteView = payload.site_view;
        const controlSummary = payload.control_summary
          ? {
              ...payload.control_summary,
              top_patterns: (siteView?.valid_scan_register ||
                payload.control_summary.top_patterns) as NonNullable<
                OverviewControlSummaryResponse['control_summary']
              >['top_patterns'],
              classification_attention_patterns: (siteView?.classification_attention ||
                payload.control_summary.classification_attention_patterns) as NonNullable<
                OverviewControlSummaryResponse['control_summary']
              >['classification_attention_patterns'],
            }
          : payload.control_summary;
        const mapped = mapOverviewControlSummaryResponse({
          ...payload,
          control_summary: controlSummary,
        });
        const nextTiming: Record<string, SiteViewTiming> = {};
        const nextMeasurement: Record<string, SiteViewScanMeasurement> = {};
        const nextPolicyId: Record<string, string> = {};
        for (const row of siteView?.valid_scan_register || []) {
          const keys = [
            String(row.scan_key || '').trim(),
            String(row.group_key || '').trim(),
            `${String(row.camera_id || '').trim()}|${String(row.zone_id || '').trim()}`,
          ].filter((key) => key && key !== '|');
          for (const key of keys) {
            if (row.timing) nextTiming[key] = row.timing;
            if (row.scan_measurement_summary) {
              nextMeasurement[key] = row.scan_measurement_summary;
            }
            if (String(row.policy_id || '').trim()) {
              nextPolicyId[key] = String(row.policy_id).trim();
            }
          }
        }
        setSummary(mapped);
        setSiteSummary(siteView?.summary);
        setTimingByKey(nextTiming);
        setMeasurementByKey(nextMeasurement);
        setPolicyIdByKey(nextPolicyId);
        setError(mapped.error || null);
      })
      .catch(() => {
        if (!cancelled) setError('Measurement diagnostics are unavailable for this site.');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const projection = useMemo(
    () => buildSiteViewProjection(summary, siteSummary),
    [siteSummary, summary],
  );
  const hotspotFilter = searchParams.get('result') === 'recurring_hotspot';
  const filterRows = (rows: OverviewControlTopPattern[]) =>
    hotspotFilter ? rows.filter((row) => row.buyerSurface === 'recurring_hotspot') : rows;
  const visibleSignedScans = filterRows(projection.signedScans);
  const visibleScansInProgress = filterRows(projection.scansInProgress);
  const visiblePatternsAwaitingScan = filterRows(projection.patternsAwaitingScan);
  const currentSiteId = String(summary?.siteId || searchParams.get('site_id') || '').trim();

  const resolveByRowKey = <T,>(
    row: OverviewControlTopPattern,
    values: Record<string, T>,
  ): T | undefined => {
    const key = String(row.groupKey || `${row.cameraId || ''}|${row.zoneId || ''}`).trim();
    if (key && values[key]) return values[key];
    // Backend scan_key is policy|camera|zone when available; fall back to exact scope suffix.
    const camera = String(row.cameraId || '').trim();
    const zone = String(row.zoneId || '').trim();
    if (!camera || !zone) return undefined;
    const suffix = `|${camera}|${zone}`;
    const match = Object.entries(values).find(([scanKey]) => scanKey.endsWith(suffix));
    return match?.[1];
  };

  const openRow = (row: OverviewControlTopPattern) => {
    const path =
      (row.activeDraftReportId ? row.safetyScanWorkspaceUrl : null) ||
      row.safetyCaseUrl ||
      row.riskPatternReviewUrl ||
      row.linkToPatternReview ||
      '/incidents?table_mode=grouped';
    navigate(path);
  };

  if (isLoading && !summary) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress aria-label="Loading measurement diagnostics" />
      </Box>
    );
  }

  if (error || summary?.error) {
    return <Alert severity="error">Measurement diagnostics are unavailable for this site.</Alert>;
  }

  return (
    <Stack spacing={2.25}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>
          Measurement diagnostics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.4 }}>
          Legacy internal view for measurement records and compatibility deep links.
          Classification attention stays outside all three.
        </Typography>
        {currentSiteId ? (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 1.5 }}>
            {canAccessScheduledScans ? (
              <Button
                variant="outlined"
                onClick={() =>
                  navigate(
                    `/acceptance/scan-definitions?site_id=${encodeURIComponent(currentSiteId)}`,
                  )
                }
              >
                Manage recurring scans
              </Button>
            ) : null}
            <Button
              variant="outlined"
              data-testid="site-view-site-condition-shell-link"
              onClick={() =>
                navigate(buildSellableShellHref({ siteId: currentSiteId, view: 'now' }))
              }
            >
              Site condition
            </Button>
          </Stack>
        ) : null}
        {hotspotFilter ? (
          <Chip
            label="Filtered: recurring hotspots requiring review"
            color="warning"
            size="small"
            sx={{ mt: 1 }}
          />
        ) : null}
      </Box>

      <Box
        data-testid="site-view-summary"
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
          gap: 1.25,
        }}
      >
        <SummaryMetric
          label="Signed Safety Control Scans"
          value={
            siteSummary?.signed_safety_control_scans ?? projection.signedScans.length
          }
        />
        <SummaryMetric
          label="Scans in progress"
          value={siteSummary?.scans_in_progress ?? projection.scansInProgress.length}
        />
        <SummaryMetric
          label="Patterns awaiting a scan"
          value={
            siteSummary?.patterns_awaiting_scan ?? projection.patternsAwaitingScan.length
          }
        />
        <SummaryMetric
          label="Classification or configuration attention"
          value={
            siteSummary?.classification_attention_count ??
            projection.attentionRecords.length
          }
        />
      </Box>

      {currentSiteId ? <SiteConditionStatePanel siteId={currentSiteId} /> : null}

      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Signed Safety Control Scans
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Finalized decision records only. Showing {visibleSignedScans.length.toLocaleString()}.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small" aria-label="Signed Safety Control Scans">
            <TableHead>
              <TableRow>
                <TableCell>Policy</TableCell>
                <TableCell>Where</TableCell>
                <TableCell>When</TableCell>
                <TableCell>Recurrence</TableCell>
                <TableCell>Next step</TableCell>
                <TableCell>Latest activity</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleSignedScans.map((row) => (
                <TableRow key={row.groupKey || `${row.cameraId}|${row.zoneId}`}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {siteViewPolicyLabel(row)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {siteViewPolicySecondary(row, resolveByRowKey(row, policyIdByKey))}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 800 }}>
                      {siteViewZoneLabel(row)}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                    >
                      {siteViewCameraLabel(row)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {formatSiteViewTimingLabel(resolveByRowKey(row, timingByKey))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current observation window
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <ScanMeasurementResult
                      measurement={resolveByRowKey(row, measurementByKey)}
                      state="signed"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={decisionStatus(row)} size="small" color="warning" />
                  </TableCell>
                  <TableCell>{latestActivity(row)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openRow(row)}>
                      {hasSafetyCaseId(row) ? 'Open case' : 'Review hotspot'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!visibleSignedScans.length ? (
                <TableRow>
                  <TableCell colSpan={7}>No signed Safety Control Scans match this view.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {[
        {
          key: 'in-progress',
          title: 'Scans in progress',
          description: 'Draft reports that have not completed review, measurement, and sign-off.',
          rows: visibleScansInProgress,
          state: 'in_progress' as const,
          nextStep: 'Complete Safety Control Scan',
          action: 'Open draft',
          empty: 'No Safety Control Scans are currently in progress.',
        },
        {
          key: 'awaiting',
          title: 'Patterns awaiting a scan',
          description: 'Recurring patterns with valid identity, but no draft or signed Safety Control Scan.',
          rows: visiblePatternsAwaitingScan,
          state: 'awaiting' as const,
          nextStep: 'Review pattern and start scan',
          action: 'Review pattern',
          empty: 'No recurring patterns are awaiting a scan.',
        },
      ].map((section) => (
        <Paper key={section.key} variant="outlined" sx={{ overflow: 'hidden' }}>
          <Box sx={{ p: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {section.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {section.description}
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small" aria-label={section.title}>
              <TableHead>
                <TableRow>
                  <TableCell>Policy</TableCell>
                  <TableCell>Where</TableCell>
                  <TableCell>When</TableCell>
                  <TableCell>Recurrence</TableCell>
                  <TableCell>Next step</TableCell>
                  <TableCell>Latest activity</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {section.rows.map((row) => (
                  <TableRow key={`${section.key}-${row.groupKey || `${row.cameraId}|${row.zoneId}`}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {siteViewPolicyLabel(row)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {siteViewPolicySecondary(row, resolveByRowKey(row, policyIdByKey))}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {siteViewZoneLabel(row)}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
                      >
                        {siteViewCameraLabel(row)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {formatSiteViewTimingLabel(resolveByRowKey(row, timingByKey))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Current observation timing
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <ScanMeasurementResult
                        measurement={resolveByRowKey(row, measurementByKey)}
                        state={section.state}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip label={section.nextStep} size="small" color="warning" />
                    </TableCell>
                    <TableCell>{latestActivity(row)}</TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => openRow(row)}>
                        {section.action}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {!section.rows.length ? (
                  <TableRow>
                    <TableCell colSpan={7}>{section.empty}</TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ))}

      <Paper variant="outlined" data-testid="site-view-attention" sx={{ overflow: 'hidden' }}>
        <Box sx={{ p: 1.75, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>
            Classification or configuration attention
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These records remain operationally visible but do not count as valid scans or recurring
            hotspots.
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small" aria-label="Classification or configuration attention">
            <TableHead>
              <TableRow>
                <TableCell>Attention reason</TableCell>
                <TableCell>Policy / grouping</TableCell>
                <TableCell>Camera</TableCell>
                <TableCell>Zone</TableCell>
                <TableCell>Safety Case</TableCell>
                <TableCell>Latest activity</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projection.attentionRecords.map((row) => (
                <TableRow key={row.groupKey || `${row.cameraId}|${row.zoneId}|attention`}>
                  <TableCell>{attentionReason(row)}</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>{siteViewPolicyLabel(row)}</TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {siteViewCameraLabel(row)}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                    {siteViewZoneLabel(row)}
                  </TableCell>
                  <TableCell>
                    {hasSafetyCaseId(row) ? 'Active Safety Case' : 'No linked Safety Case'}
                  </TableCell>
                  <TableCell>{latestActivity(row)}</TableCell>
                  <TableCell align="right">
                    <Button size="small" onClick={() => openRow(row)}>
                      {hasSafetyCaseId(row) ? 'Open case' : 'Repair configuration'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!projection.attentionRecords.length ? (
                <TableRow>
                  <TableCell colSpan={7}>No classification or configuration attention.</TableCell>
                </TableRow>
              ) : null}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
};

export default AcceptanceSiteView;
